/**
 * Document Upload Service
 * Handles encrypted file uploads to Supabase Storage
 * Uses symmetric AES-256-GCM encryption with master key from session
 */

import { supabase } from '@xanectech/supabase-client';
import {
  getCachedMasterKey,
  generateDocumentKey,
  encryptFileBinary,
  wrapDocumentKeyWithMaster,
} from '@xanectech/encryption';
import { createLogger } from '@xanectech/lib';
import type {
  UserDocument,
  UploadProgress,
  UploadResult,
  UploadOptions,
} from '@xanectech/types';

const logger = createLogger('DocumentUploadService');

/**
 * Check if user can upload (subscription + quota check)
 */
export async function canUpload(
  fileSize: number,
): Promise<{ allowed: boolean; reason?: string }> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return { allowed: false, reason: 'Not authenticated' };

  const { data, error } = await supabase.rpc('can_upload_document', {
    p_user_id: user.user.id,
    p_file_size_bytes: fileSize,
  });

  if (error) {
    logger.error('Failed to check upload permission', error);
    return { allowed: false, reason: 'Failed to verify upload permission' };
  }

  if (!data) {
    return {
      allowed: false,
      reason: 'Storage quota exceeded or subscription required',
    };
  }

  return { allowed: true };
}

/**
 * Upload a document with encryption
 */
export async function uploadDocument(
  options: UploadOptions,
): Promise<UploadResult> {
  const { file, folderId, title, description, documentType, tags, onProgress } =
    options;

  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      throw new Error('Not authenticated');
    }
    const userId = userData.user.id;

    const uploadCheck = await canUpload(file.size);
    if (!uploadCheck.allowed) {
      throw new Error(uploadCheck.reason);
    }

    onProgress?.({ loaded: 0, total: file.size, percentage: 0 });

    // Get the master key from the encryption session
    const masterKey = getCachedMasterKey(userId);
    if (!masterKey) {
      throw new Error(
        'Encryption session not active. Please unlock your vault first.',
      );
    }

    // Generate a unique document encryption key
    const documentKey = await generateDocumentKey();

    onProgress?.({ loaded: file.size * 0.1, total: file.size, percentage: 10 });

    // Encrypt the file with document key
    const fileData = await file.arrayBuffer();
    const { encryptedData, iv } = await encryptFileBinary(
      fileData,
      documentKey,
    );

    onProgress?.({ loaded: file.size * 0.3, total: file.size, percentage: 30 });

    // Wrap the document key with the master key
    const { encryptedKey, keyIv } = await wrapDocumentKeyWithMaster(
      documentKey,
      masterKey,
    );

    // Combine encrypted key and its IV for storage
    const encryptedKeyBundle = JSON.stringify({ encryptedKey, keyIv });

    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const timestamp = Date.now();
    const randomId = crypto.randomUUID();
    const storagePath = `${userId}/${timestamp}-${randomId}.enc`;


    onProgress?.({ loaded: file.size * 0.4, total: file.size, percentage: 40 });
    const { error: uploadError } = await supabase.storage
      .from('user-documents')
      .upload(storagePath, new Blob([encryptedData]), {
        contentType: file.type, //'application/octet-stream',
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      logger.error('Failed to upload file to storage', uploadError);
      throw new Error('Failed to upload file');
    }

    onProgress?.({ loaded: file.size * 0.8, total: file.size, percentage: 80 });

    const { data: document, error: dbError } = await supabase
      .from('user_documents')
      .insert({
        user_id: userId,
        folder_id: folderId || null,
        file_name: file.name,
        file_extension: fileExtension || null,
        mime_type: file.type || null,
        file_size_bytes: file.size,
        storage_path: storagePath,
        storage_bucket: 'user-documents',
        is_encrypted: true,
        encryption_algorithm: 'AES-256-GCM',
        encrypted_key: encryptedKeyBundle,
        iv,
        document_type: documentType || null,
        title: title || file.name,
        description: description || null,
        tags: tags || null,
      })
      .select()
      .single();

    if (dbError) {
      await supabase.storage.from('user-documents').remove([storagePath]);
      logger.error('Failed to create document record', dbError);
      throw new Error('Failed to save document metadata');
    }

    onProgress?.({ loaded: file.size, total: file.size, percentage: 100 });


    // Transform the response
    const transformedDoc: UserDocument = {
      id: document.id,
      user_id: document.user_id,
      folder_id: document.folder_id,
      file_name: document.file_name,
      file_extension: document.file_extension,
      mime_type: document.mime_type,
      file_size_bytes: document.file_size_bytes,
      storage_path: document.storage_path,
      storage_bucket: document.storage_bucket ?? 'user-documents',
      is_encrypted: document.is_encrypted ?? true,
      encryption_algorithm: document.encryption_algorithm || 'AES-256-GCM',
      encrypted_key: document.encrypted_key,
      iv: document.iv,
      document_type: document.document_type,
      title: document.title,
      description: document.description,
      tags: document.tags,
      version: document.version ?? 1,
      parent_version_id: document.parent_version_id,
      is_latest_version: document.is_latest_version ?? true,
      status: document.status ?? 'active',
      archived_at: document.archived_at,
      deleted_at: document.deleted_at,
      created_at: document.created_at ?? new Date().toISOString(),
      updated_at: document.updated_at ?? new Date().toISOString(),
      last_accessed_at: document.last_accessed_at,
    };

    return { document: transformedDoc, success: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Upload failed';
    logger.error(
      'Document upload failed',
      error instanceof Error ? error : { errorMessage: String(error) },
    );
    return {
      document: null,
      success: false,
      error: message,
    };
  }
}

/**
 * Upload multiple documents
 */
export async function uploadDocuments(
  files: File[],
  options: Omit<UploadOptions, 'file'> & {
    onFileProgress?: (fileIndex: number, progress: UploadProgress) => void;
    onFileComplete?: (fileIndex: number, result: UploadResult) => void;
  },
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (!file) continue;

    const result = await uploadDocument({
      ...options,
      file,
      onProgress: (progress) => options.onFileProgress?.(i, progress),
    });

    results.push(result);
    options.onFileComplete?.(i, result);
  }

  return results;
}
